import { useState } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, X, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';

interface HelpStep {
  title: string;
  description: string;
  image?: string;
}

const adminHelpSteps: HelpStep[] = [
  {
    title: 'Willkommen im Admin-Dashboard',
    description: 'Hier verwalten Sie alle Audit-Submissions und Leads. Lassen Sie uns die wichtigsten Features durchgehen.',
  },
  {
    title: 'Übersicht-Tab',
    description: 'Sehen Sie KPIs auf einen Blick: Heute\'s Audits, Wochenstatistik, durchschnittlicher Score und Hot Leads.',
  },
  {
    title: 'Submissions verwalten',
    description: 'Im Submissions-Tab finden Sie alle Audits. Nutzen Sie die Filter um schnell die richtigen Leads zu finden.',
  },
  {
    title: 'Status & Priorität',
    description: 'Setzen Sie den Status (Neu, Kontaktiert, Qualifiziert, Gewonnen, Verloren) und die Priorität (Hot, Warm, Cold) für jeden Lead.',
  },
  {
    title: 'Notizen hinzufügen',
    description: 'Klicken Sie auf das Nachrichten-Icon 💬 um Notizen zu einem Lead hinzuzufügen. Perfekt für Follow-up Informationen.',
  },
  {
    title: 'Detail-Ansicht',
    description: 'Klicken Sie auf das Augen-Icon 👁️ um alle Details zu sehen: Unternehmensdaten, Audit-Ergebnisse und alle Fragen & Antworten.',
  },
  {
    title: 'Export-Funktionen',
    description: 'Exportieren Sie Daten als CSV (für Excel) oder JSON (für weitere Verarbeitung). Perfekt für Reporting und Analyse.',
  },
  {
    title: 'Analytics-Tab',
    description: 'Analysieren Sie Trends: Branchen-Verteilung, Score-Bereiche und Kategorie-Performance über alle Audits.',
  },
];

export function AdminHelp() {
  const [showHelp, setShowHelp] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < adminHelpSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowHelp(false);
      setCurrentStep(0);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentHelpStep = adminHelpSteps[currentStep];

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowHelp(true)}
        className="fixed bottom-6 right-6 z-50 shadow-lg"
      >
        <HelpCircle className="w-4 h-4 mr-2" />
        Hilfe
      </Button>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Admin-Dashboard Hilfe</span>
              <span className="text-sm font-normal text-muted-foreground">
                {currentStep + 1} / {adminHelpSteps.length}
              </span>
            </DialogTitle>
            <DialogDescription>
              Lernen Sie die wichtigsten Features kennen
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-3">
                    {currentHelpStep.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {currentHelpStep.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Progress Dots */}
            <div className="flex items-center justify-center gap-2">
              {adminHelpSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'bg-primary w-8'
                      : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                Zurück
              </Button>
              <Button onClick={handleNext}>
                {currentStep === adminHelpSteps.length - 1 ? (
                  <>
                    <X className="w-4 h-4 mr-2" />
                    Fertig
                  </>
                ) : (
                  <>
                    Weiter
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
